import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Outlet, generatePath, useLocation, useNavigate, useParams } from 'react-router';
import { Box } from '@mui/material';
import { UnitDetailTopbar } from 'components/layout/UnitDetailTopbar';
import { HmiMenuBackButton } from 'components/layout/UnitDetailTopbar/HmiMenuBackButton';
import { CHAT_DEFAULT_WIDTH } from 'features/assetControl/assetControl.constants';
import { OperatorChat } from 'features/assetControl/components/OperatorChat';
import { COMMAND_CENTER_TAB_PARAM } from 'features/hmi/hmi.utils';
import useAuth from 'hooks/useAuth';
import { useBreakpoint } from 'hooks/useBreakpoint';
import useProject from 'hooks/useProject';
import { getDefaultRangeInHours, useTimerange } from 'hooks/useTimerange';
import { useUnitDetailTopbarState } from 'hooks/useUnitDetailTopbarState';
import useUnitsConnectionStatus from 'hooks/useUnitsConnectionStatus';
import { useUrlState } from 'hooks/useUrlState';
import { GreenerUnit } from 'types';
import { PROJECT_UNIT_INSTANCES_DATE_STATUS } from 'utils/constants';
import { getDateStatus } from 'utils/dates';
import { getAssetLabel } from 'utils/formatters';
import { determineProjectDateRange, getSortedByActivityUnits, isBatteryAsset } from 'utils/units';

// Rendered only when a specific unit is selected (single-unit monitoring sub-routes).
// Kept as a separate component so that useTimerange is never mounted for sub-routes like
// multi-battery-graphs that manage their own range — avoiding URL param conflicts.
function ProjectMonitoringUnitLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectSlug = '', unitExternalIdentifier = '' } = useParams();
  const { projectUnits: units, projectUnitInstances: instances, project } = useProject();
  const { user } = useAuth();
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();
  const { getUrlState } = useUrlState();

  const selectedAsset = units.find((unit) => unit.externalIdentifier === unitExternalIdentifier);

  const {
    postFix,
    pageTitleKey,
    isHmiTabPage,
    isChatVisible,
    isChatSplit,
    setIsChatSplit,
    isOpen,
    setIsOpen,
    rightTopbarSlot,
    setRightTopbarSlot,
  } = useUnitDetailTopbarState({ pathname: location.pathname, asset: selectedAsset });

  const pageTitle = pageTitleKey ? t(pageTitleKey) : undefined;

  const leftTopbarSlot =
    isMobile && isHmiTabPage ? (
      <HmiMenuBackButton
        onClick={() =>
          navigate(
            COMMAND_CENTER_TAB_PARAM in getUrlState()
              ? location.pathname
              : generatePath('/projects/:projectSlug/monitoring/:unitExternalIdentifier/hmi/menu', {
                  projectSlug,
                  unitExternalIdentifier,
                })
          )
        }
      />
    ) : null;

  const availableRange = determineProjectDateRange(project);
  const { range, ...timerangeProps } = useTimerange(
    availableRange,
    getDefaultRangeInHours(availableRange),
    !location.pathname.includes('/hmi')
  );

  function getOnChangeUnitPath({ unit, pathPostFix }: { unit: GreenerUnit; pathPostFix: string }) {
    const basePath = '/projects/:projectSlug/monitoring/:unitExternalIdentifier';

    if (location.pathname.includes('/hmi/') && !isBatteryAsset(unit)) {
      return generatePath(`${basePath}/asset`, {
        projectSlug,
        unitExternalIdentifier: unit.externalIdentifier,
      });
    }

    if (location.pathname.includes('/hmi/')) {
      return generatePath(`${basePath}/hmi/:tab`, {
        projectSlug,
        unitExternalIdentifier: unit.externalIdentifier,
        tab: pathPostFix,
      });
    }

    return generatePath(`${basePath}/${pathPostFix}`, {
      projectSlug,
      unitExternalIdentifier: unit.externalIdentifier,
    });
  }

  function createOnChangeUnitFunction(pathPostFix: string) {
    function onChangeUnit(unit: GreenerUnit) {
      navigate(getOnChangeUnitPath({ unit, pathPostFix }), { replace: true });
    }
    return onChangeUnit;
  }
  const isAssetOwnedByUsersOrganisation = (asset, user) =>
    asset?.owningOrganisation?.organisationId === user?.organisation.organisationId;

  const isOnline = useUnitsConnectionStatus()?.[selectedAsset?.externalIdentifier ?? ''] ?? false;
  const sortedUnits = getSortedByActivityUnits(instances ?? []);

  const unitInstance = instances?.find((i) => i.greenerUnit?.externalIdentifier === unitExternalIdentifier);
  const isLiveDataEnabled =
    isAssetOwnedByUsersOrganisation(selectedAsset, user) ||
    (!!unitInstance &&
      getDateStatus(unitInstance.tsStart, unitInstance.tsEnd) === PROJECT_UNIT_INSTANCES_DATE_STATUS.active);

  const isHmiPage = location.pathname.includes('/hmi');

  return (
    <>
      <Helmet>
        <title>{project?.name}</title>
      </Helmet>
      <Box
        sx={{
          transition: 'margin-right 0.3s',
          marginRight: !isMobile && isChatSplit ? `${CHAT_DEFAULT_WIDTH}px` : 0,
        }}
      >
        <UnitDetailTopbar
          title={pageTitle}
          rightSlot={rightTopbarSlot}
          leftSlot={leftTopbarSlot}
          isSticky={isMobile && isHmiPage}
          unitSelectorProps={{
            units: sortedUnits,
            onChange: createOnChangeUnitFunction(postFix),
            selectedUnitExtId: unitExternalIdentifier,
          }}
          timerangePickerProps={{
            range,
            availableRange,
            ...timerangeProps,
          }}
          showRangePicker={!location.pathname.includes('asset') && !location.pathname.includes('/hmi')}
        />
        <Outlet
          key={unitExternalIdentifier}
          context={{
            selectedAsset,
            assetLabels: [getAssetLabel(selectedAsset)],
            range,
            isOnline,
            isLiveDataEnabled,
            setRightTopbarSlot,
          }}
        />
      </Box>

      {isChatVisible && (
        <OperatorChat
          unit={selectedAsset!}
          isOnline={isOnline}
          isChatSplit={isChatSplit}
          setIsChatSplit={setIsChatSplit}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      )}
    </>
  );
}

export const ProjectMonitoringLayout = () => {
  const { unitExternalIdentifier } = useParams();

  if (!unitExternalIdentifier) {
    return <Outlet />;
  }

  return <ProjectMonitoringUnitLayout />;
};
